import { OrganizationUser } from '@enview/interface/types/OrganizationUser';
import fflip from 'fflip';

export enum FrontendFeature {
  BillTextDebug = 'bill-text-debug',
  BillVersionSourceLink = 'bill-version-source-link',
  BillSimilarBillsRelevanceSwitch = 'bill-similar-bills-relevance-switch',
  CollapsibleSideMenu = 'collapsible-sidemenu',
  BillTableView = 'bill-table-view',
  RegulationsSearch = 'regulations-search',
  JurisdictionOverviewRedesign = 'jurisdiction-overview-redesign',
  NewJurisdictionSelectorForRegs = 'new-jurisdiction-selector-for-regs',
  LegislatorStaffDirectoryTab = 'legislator-staff-directory-tab',
  CommitteeStaffDirectoryTab = 'committee-staff-directory-tab',
  JurisdictionHearings = 'jurisdiction-hearings',
  EndOfSessionReport = 'end-of-session-report',
  isCanViewCemlInMissingJurisdictions = 'can-view-ceml-in-missing-jurisdictions',
  BillPositionStatement = 'bill-position-statement',
  WorkspaceManagementView = 'workspace-management-view',
  AgentsSidebarSection = 'agents-sidebar-section',
  ExperimentalDCSummaries = 'experimental-dc-summaries',
  AIBillSearchDemo = 'AI-bill-search-demo',
  HearingsNotificationSetting = 'hearings-notification-setting',
  AgentsLibrary = 'agents-library',
  AISearch = 'AI-bill-search',
  hearingsDashboard = 'hearings-dashboard',
  CustomFields = 'custom-fields',
  TanstackTable = 'tanstack-table',
}

const isDev = process.env.REACT_APP_ENV?.toLowerCase() === 'dev';
const isProd = process.env.REACT_APP_ENV?.toLowerCase() === 'prod';
const isLocal = process.env.REACT_APP_ENV?.toLowerCase() === undefined;

const mplOrgsIDs = [3, 4, 5, 6, 7, 11, 12, 13, 14, 17, 18];
const testOrgIDs = [165];
const labsDevID = 336;
const labsProdID = 8223;

fflip.config({
  criteria: [
    {
      id: 'isCivicEagleStaff',
      check: (user: OrganizationUser | undefined) => {
        if (!user) return false;
        if (isLocal) {
          const email = user.email ?? '';
          return (
            email.includes('@civiceagle.org') || email.includes('@pluralpolicy.com')
          );
        }
        return user.organizations.map((o) => o.id).includes(1);
      },
    },
    {
      id: 'isMPLOrgsUser',
      check: (user: OrganizationUser | undefined) =>
        user && !!user.organizations.find((org) => mplOrgsIDs.includes(org.id)),
    },
    {
      id: 'isAutomatedTestingUser',
      check: (user: OrganizationUser | undefined) =>
        user &&
        !!user.organizations.find((org) => testOrgIDs.includes(org.id)) &&
        isDev,
    },
    // "Labs" is intended for demos and experimental features that we want
    // to use in sales demos and similar contexts
    {
      id: 'isLabsOrgUser',
      check: (user: OrganizationUser | undefined) =>
        user &&
        (!!user.organizations.find((org) => org.id === labsDevID && isDev) ||
          !!user.organizations.find((org) => org.id === labsProdID && isProd) ||
          !!user?.organizations.find((org) => org.name === 'Plural Labs' && isLocal)),
    },
    // As of Q3 2025, Demoing certain features for a Comcast Local team in Prod
    {
      id: 'isComcastLocalTrial',
      check: (user: OrganizationUser | undefined) =>
        user && !!user.organizations.find((org) => org.id === 8391),
    },
    {
      id: 'isDevEnvironment',
      check: () => isDev || isLocal,
    },
    {
      id: 'isCanViewCemlInMissingJurisdictions',
      check: (user: OrganizationUser | undefined) =>
        user &&
        user.teamMemberships.find(
          (t) => t.team.name === FrontendFeature.isCanViewCemlInMissingJurisdictions,
        ),
    },
    {
      id: 'anyone',
      check: (anyone: any) => anyone,
    },
    {
      id: 'nobody',
      check: () => false,
    },
  ],
  // If criteria is an object, ALL criteria must evaluate to true to enable feature flag
  // If criteria is an array, ANY ONE set of criteria must evaluate to true to enable feature flag
  features: [
    {
      id: FrontendFeature.BillTextDebug,
      criteria: { isCivicEagleStaff: true },
    },
    {
      id: FrontendFeature.BillVersionSourceLink,
      criteria: { isCivicEagleStaff: true },
    },
    {
      id: FrontendFeature.BillSimilarBillsRelevanceSwitch,
      criteria: { isCivicEagleStaff: true },
    },
    {
      id: FrontendFeature.CollapsibleSideMenu,
      criteria: [{ isCivicEagleStaff: true }, { isAutomatedTestingUser: true }],
    },
    {
      id: FrontendFeature.RegulationsSearch,
      criteria: [{ isCivicEagleStaff: true }, { isAutomatedTestingUser: true }],
    },
    {
      // TODO: Get this rolled out for internal users when regs testing resumes
      id: FrontendFeature.NewJurisdictionSelectorForRegs,
      criteria: [{ isAutomatedTestingUser: true }],
    },
    {
      id: FrontendFeature.JurisdictionOverviewRedesign,
      criteria: { anyone: true },
    },
    {
      id: FrontendFeature.LegislatorStaffDirectoryTab,
      criteria: { anyone: true },
    },
    {
      id: FrontendFeature.CommitteeStaffDirectoryTab,
      criteria: { anyone: true },
    },
    {
      id: FrontendFeature.JurisdictionHearings,
      criteria: { anyone: true },
    },
    {
      id: FrontendFeature.EndOfSessionReport,
      criteria: { anyone: true },
    },
    {
      id: FrontendFeature.isCanViewCemlInMissingJurisdictions,
      criteria: [{ isCanViewCemlInMissingJurisdictions: true }],
    },
    {
      id: FrontendFeature.BillPositionStatement,
      criteria: { anyone: true },
    },
    {
      id: FrontendFeature.WorkspaceManagementView,
      criteria: [{ isCivicEagleStaff: true }, { isAutomatedTestingUser: true }],
    },
    {
      id: FrontendFeature.AgentsSidebarSection,
      criteria: { isLabsOrgUser: true },
    },
    {
      id: FrontendFeature.AgentsLibrary,
      criteria: { isLabsOrgUser: true },
    },
    {
      id: FrontendFeature.ExperimentalDCSummaries,
      criteria: [
        { isCivicEagleStaff: true },
        { isAutomatedTestingUser: true },
        { isLabsOrgUser: true },
        { isComcastLocalTrial: true },
      ],
    },
    {
      id: FrontendFeature.AIBillSearchDemo,
      criteria: [{ isAutomatedTestingUser: true }, { isLabsOrgUser: true }],
    },
    {
      id: FrontendFeature.HearingsNotificationSetting,
      criteria: { anyone: true },
    },
    {
      id: FrontendFeature.AISearch,
      criteria: { isLabsOrgUser: true },
    },
    {
      id: FrontendFeature.hearingsDashboard,
      criteria: { anyone: true },
    },
    {
      id: FrontendFeature.CustomFields,
      criteria: { isCivicEagleStaff: true },
    },
    {
      id: FrontendFeature.TanstackTable,
      criteria: { isCivicEagleStaff: true },
    },
  ],
});

export default {
  getFeaturesForUser: (user: any) => fflip.getFeaturesForUser(user),
  isFeatureEnabledForUser: (featureName: FrontendFeature, user: any) => {
    const isEnabled = fflip.isFeatureEnabledForUser(featureName, user);
    if (isEnabled === undefined || isEnabled === null) {
      throw new Error(`Feature flag ${featureName} configuration not found.`);
    }

    return isEnabled;
  },
};
